import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Image, GripVertical } from 'lucide-react'
import { collectionsApi } from '../../lib/api.js'
import type { Collection } from '../../types/domain.js'
import type { Program } from '../../types/index.js'
import { formatDurationShort, isBroadcastableMedia } from '../../lib/utils.js'
import { updateRangeSelection } from '../../lib/range-selection.js'
import { ProgramBulkActions } from './ProgramBulkActions.js'
import { Checkbox } from '../ui/checkbox.js'
import { Spinner } from '../common/Spinner.js'
import { moveInOrder } from './collectionOrder.js'

/**
 * Fiche d'une collection : son contenu, dans l'ordre éditorial que l'opérateur
 * y a posé.
 *
 * Une collection n'est pas qu'un filtre : l'ordre des programmes y a un sens
 * (un cycle, un classement, une suite). Il vit sur la ligne de jonction et se
 * relit tel quel à l'affichage — d'où le réordonnancement ici, absent tant que
 * la collection ne servait qu'à filtrer le catalogue.
 *
 * Comme partout ailleurs : barre de recherche et sélection multiple.
 */

interface CollectionDetailProps {
  collection:      Collection
  channelId:       string
  canManage:       boolean
  onSelectProgram: (program: Program) => void
  onBack:          () => void
  /** Ouvre le catalogue filtré sur cette collection, pour y ajouter des fiches. */
  onShowInCatalog: (collection: Collection) => void
}

export const CollectionDetail = memo(function CollectionDetail({
  collection, channelId, canManage, onSelectProgram, onBack, onShowInCatalog,
}: CollectionDetailProps) {
  const [programs,    setPrograms]    = useState<Program[]>([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState<string | null>(null)
  const [search,      setSearch]      = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [savingOrder, setSavingOrder] = useState(false)
  const selectionAnchorRef            = useRef<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const detail = await collectionsApi.getById(collection.id)
      setPrograms(detail.programs)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Chargement impossible')
    } finally {
      setLoading(false)
    }
  }, [collection.id])

  useEffect(() => { void reload() }, [reload])

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return programs
    return programs.filter(program => program.title.toLowerCase().includes(needle))
  }, [programs, search])

  const visibleIds = useMemo(() => filtered.map(program => program.id), [filtered])

  const handleCheck = useCallback((program: Program, checked: boolean, shiftKey: boolean) => {
    setSelectedIds(previous => shiftKey
      ? updateRangeSelection(visibleIds, previous, selectionAnchorRef.current, program.id, checked)
      : (() => {
          const next = new Set(previous)
          if (checked) next.add(program.id)
          else next.delete(program.id)
          return next
        })())
    if (!shiftKey) selectionAnchorRef.current = program.id
  }, [visibleIds])

  const allSelected  = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id))
  const someSelected = visibleIds.some(id => selectedIds.has(id))

  const handleCheckAll = useCallback(() => {
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds))
    selectionAnchorRef.current = null
  }, [allSelected, visibleIds])

  const handleDone = useCallback(() => {
    setSelectedIds(new Set())
    selectionAnchorRef.current = null
    void reload()
  }, [reload])

  /**
   * Déplacement d'un cran.
   *
   * Le réordonnancement porte sur la liste **complète**, jamais sur le résultat
   * d'une recherche : l'API exige une permutation exacte, et « monter » dans une
   * liste filtrée n'aurait de toute façon pas de sens — la fiche remonterait
   * au-dessus d'un voisin que l'opérateur ne voit pas.
   */
  const move = useCallback(async (programId: string, direction: -1 | 1) => {
    const next = moveInOrder(programs.map(program => program.id), programId, direction)
    if (!next) return
    const previous = programs
    // Optimiste : l'ordre se repose tout de suite, et se rétablit si l'API
    // refuse. Attendre l'aller-retour rendrait le réglage d'une longue liste
    // pénible.
    setPrograms(next.map(id => programs.find(program => program.id === id)!))
    setSavingOrder(true)
    setError(null)
    try {
      await collectionsApi.reorder(collection.id, next)
    } catch (cause) {
      setPrograms(previous)
      setError(cause instanceof Error ? cause.message : 'Réordonnancement impossible')
    } finally {
      setSavingOrder(false)
    }
  }, [collection.id, programs])

  const handleRemove = useCallback(async (program: Program) => {
    if (!window.confirm(`Retirer « ${program.title} » de la collection ?\n\nLe programme reste au catalogue.`)) return
    try {
      await collectionsApi.removeProgram(collection.id, program.id)
      void reload()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Retrait impossible')
    }
  }, [collection.id, reload])

  const accentStyle = collection.color
    ? { '--catalog-accent': collection.color } as CSSProperties
    : undefined

  const totalDuration = useMemo(
    () => programs.reduce((total, program) => total + (program.totalDurationSec ?? program.durationSec), 0),
    [programs],
  )

  const searching = search.trim().length > 0

  return (
    <div className="programs-overview collection-detail">
      <div className="series-detail__back">
        <button type="button" className="tp-button" onClick={onBack}>← Toutes les collections</button>
      </div>

      <header className="series-detail__header">
        <div className="series-detail__identity">
          <h1 className="series-detail__title">
            <span className="catalog-collection-row__dot" style={accentStyle} aria-hidden="true" />
            {collection.name}
          </h1>
          {collection.description && (
            <p className="series-detail__description">{collection.description}</p>
          )}
        </div>
        <div className="series-detail__actions">
          <button
            type="button"
            className="tp-button tp-button--info"
            onClick={() => onShowInCatalog(collection)}
          >
            Ajouter des programmes
          </button>
        </div>
      </header>

      <div className="programs-overview__counters">
        <Counter label="Programmes" value={String(programs.length)} detail={`${formatDurationShort(totalDuration)} cumulées`} />
      </div>

      {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}

      <section aria-label="Programmes de la collection" className="series-episodes">
        <h2 className="programs-overview__heading">
          Contenu
          <span className="programs-overview__count">
            {searching ? `${filtered.length} / ${programs.length}` : String(programs.length)}
          </span>
        </h2>

        <div className="series-episodes__toolbar">
          <input
            type="search"
            className="tp-input series-episodes__search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Rechercher un programme…"
            aria-label="Rechercher un programme de la collection"
          />
        </div>

        <div className="series-episodes__count">
          <Checkbox
            className="catalog-selection-checkbox"
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            disabled={filtered.length === 0}
            onClick={handleCheckAll}
            aria-label="Sélectionner tous les programmes affichés"
          />
          <span role="status">
            {loading
              ? <><Spinner /> Chargement…</>
              : selectedIds.size > 0
                ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? 's' : ''} · ${filtered.length} affiché${filtered.length > 1 ? 's' : ''}`
                : `${filtered.length} programme${filtered.length > 1 ? 's' : ''}`}
          </span>
          {selectedIds.size > 0 && (
            <ProgramBulkActions
              channelId={channelId}
              selectedIds={[...selectedIds]}
              canDelete={canManage}
              onDone={handleDone}
            />
          )}
        </div>

        {loading ? null : filtered.length === 0 ? (
          <p className="programs-overview__empty">
            {searching
              ? 'Aucun programme ne correspond à cette recherche.'
              : 'Cette collection est vide. Ajoutez-y des programmes depuis le catalogue.'}
          </p>
        ) : (
          <ul className="series-episodes__list">
            {filtered.map((program, index) => (
              <CollectionRow
                key={program.id}
                program={program}
                position={programs.findIndex(row => row.id === program.id)}
                total={programs.length}
                checked={selectedIds.has(program.id)}
                // Réordonner une liste filtrée déplacerait la fiche au-dessus
                // d'un voisin invisible : le geste n'est offert que sur la
                // liste complète.
                canReorder={canManage && !searching && !savingOrder}
                canManage={canManage}
                onCheck={handleCheck}
                onSelect={onSelectProgram}
                onMove={move}
                onRemove={handleRemove}
                displayIndex={index}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  )
})

const Counter = memo(function Counter({ label, value, detail }: {
  label: string; value: string; detail: string
}) {
  return (
    <div className="programs-counter">
      <div className="programs-counter__label">{label}</div>
      <div className="programs-counter__value">{value}</div>
      <div className="programs-counter__detail">{detail}</div>
    </div>
  )
})

const CollectionRow = memo(function CollectionRow({
  program, position, total, checked, canReorder, canManage, onCheck, onSelect, onMove, onRemove, displayIndex,
}: {
  program:      Program
  /** Rang dans la liste complète, base du déplacement. */
  position:     number
  total:        number
  checked:      boolean
  canReorder:   boolean
  canManage:    boolean
  onCheck:      (program: Program, checked: boolean, shiftKey: boolean) => void
  onSelect:     (program: Program) => void
  onMove:       (programId: string, direction: -1 | 1) => void
  onRemove:     (program: Program) => void
  /** Rang affiché, qui suit la recherche. */
  displayIndex: number
}) {
  const media = [program, ...(program.parts ?? [])]
  const ready = media.every(part => part.mediaAssets?.some(asset => isBroadcastableMedia(asset.status)))

  return (
    <li className={`series-episodes__row${checked ? ' is-selected' : ''}`}>
      <Checkbox
        className="catalog-selection-checkbox"
        checked={checked}
        onClick={event => {
          event.stopPropagation()
          onCheck(program, !checked, event.shiftKey)
        }}
        aria-label={`Sélectionner ${program.title}`}
      />

      <span className="collection-detail__rank" aria-hidden="true">{displayIndex + 1}</span>

      <button
        type="button"
        className="series-episodes__open"
        onClick={() => onSelect(program)}
        title={program.title}
      >
        <span className="series-episodes__thumb">
          {program.thumbnailUrl
            ? <img src={program.thumbnailUrl} alt="" loading="lazy" />
            : <Image size={14} aria-hidden="true" />}
        </span>
        <span className="series-episodes__title">{program.title}</span>
        <span className="series-episodes__meta">
          {formatDurationShort(program.totalDurationSec ?? program.durationSec)}
        </span>
        {!ready && !program.isLive && <span className="series-episodes__flag">SANS MÉDIA</span>}
      </button>

      <div className="catalog-row-actions collection-detail__actions">
        <span className="collection-detail__grip" aria-hidden="true"><GripVertical size={13} /></span>
        <button
          type="button"
          className="catalog-icon-action"
          disabled={!canReorder || position <= 0}
          onClick={() => onMove(program.id, -1)}
          aria-label={`Monter ${program.title}`}
          title={canReorder ? 'Monter' : 'Réordonnancement indisponible pendant une recherche'}
        >
          ↑
        </button>
        <button
          type="button"
          className="catalog-icon-action"
          disabled={!canReorder || position >= total - 1}
          onClick={() => onMove(program.id, 1)}
          aria-label={`Descendre ${program.title}`}
          title={canReorder ? 'Descendre' : 'Réordonnancement indisponible pendant une recherche'}
        >
          ↓
        </button>
        <button
          type="button"
          className="catalog-icon-action catalog-icon-action--danger"
          disabled={!canManage}
          onClick={() => onRemove(program)}
          aria-label={`Retirer ${program.title} de la collection`}
          title={canManage ? 'Retirer de la collection' : 'Permission requise'}
        >
          ×
        </button>
      </div>
    </li>
  )
})
